/**
 * Asking a model a question, and waiting for it properly.
 *
 * Every line here was the commercial reader's, and none of it is knowledge. It
 * is what a browser does while an endpoint is thinking: which shape of request
 * to try, how long to wait for each, when to stop climbing, and how many
 * questions to have outstanding at once. It would be the same code for any
 * endpoint answering any question, which is why it is here rather than copied
 * into a second reader.
 *
 * What is NOT here is what to ask and what an answer has to prove. Those are
 * each reader's own, they are the product, and they stay server-side.
 *
 * Three things in it were each bought with a failure on a real document, and
 * the comments below keep the receipts: the rung ladder exists because an
 * endpoint that drops an unsupported parameter looks like success and reads
 * like prose; the per-attempt clock exists because one dropped socket ended a
 * whole document; and the remembered rung exists because discovering the same
 * fact about one endpoint fifteen times cost three quarters of an hour.
 */
import type { LoadedDocument } from '@asvise/read'

/** How long to wait, in one read, for the whole document. */
export const DEADLINE_MS = 1_800_000

/**
 * What one question is allowed to take, and it is two numbers rather than one.
 *
 * A rung that has answered before gets four minutes, because it is going to
 * work and the model is simply thinking. A rung nobody has seen answer gets
 * one, because the likely outcome is a refusal and the next rung is where the
 * answer is.
 */
const PROVEN_MS = 240_000
const PROBE_MS = 60_000

/**
 * Each attempt and its timing, on stderr, for whoever is diagnosing a read.
 *
 * Off unless asked for, and asked for by the scripts rather than by the app. It
 * exists because a read that produces a poor policy and a read that spent four
 * minutes being refused twice look identical from the outside, and the first
 * real trial was three quarters of an hour of not knowing which was happening.
 */
const TRACE = typeof process !== 'undefined' && process.env?.EXTRACT_TRACE === '1'

/**
 * One request to this origin, carrying the caller rather than a model key.
 *
 * The endpoint is this app's own, and what it wants to know is who is asking,
 * so it can refuse a stranger and count what an account has spent. The key it
 * spends is its own and the page never sees it.
 */
export type Caller = (path: string, body: unknown, signal?: AbortSignal) => Promise<Response>

/**
 * One request to an app's own endpoint, carrying the caller rather than a model
 * key.
 *
 * The endpoint is the app's own, and what it wants to know is who is asking, so
 * it can refuse a stranger and count what an account has spent. The key it
 * spends is its own and the page never sees it.
 *
 * Which origin and which session are the app's to say, which is the only reason
 * this takes arguments at all.
 */
export function callerFor(endpoint: string, token: () => Promise<string | null>): Caller {
  return async (path, body, signal) => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    const bearer = await token()
    if (bearer) headers.Authorization = `Bearer ${bearer}`

    return fetch(`${endpoint}/${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal,
    })
  }
}

/**
 * One question, named rather than written.
 *
 * What is asked lives in functions/extract/prompts.ts and is built there. This
 * says which pass and which cover; the endpoint decides what to put in front of
 * the model, which pages to send it, and what the reply has to look like to be
 * worth returning. The wording and the validators are the product, and a page
 * cannot hold them.
 */
export interface Ask<Section> {
  pass: 'index' | 'detail'
  /**
   * Which part of the document, for a detail pass, as the index pass reported
   * it.
   *
   * Generic because the two readers name a part differently: a commercial
   * schedule has sections of cover, a life schedule has benefits against a
   * person. What they have in common is that the index pass found one and the
   * detail pass asks about it, which is all this needs to know.
   */
  section?: Section
}

/**
 * What an endpoint says back about one pass.
 *
 * Two outcomes rather than a value or a throw, because the ladder turns on the
 * difference. `read` is a reply that arrived, parsed and passed validation.
 * `unreadable` is one that did not, and it is a 200 rather than an error
 * because the request was fine: the model answered with prose, or with
 * whitespace, or with reasoning and no answer, and the remedy is the next rung
 * rather than the next document.
 */
export interface Answered<T> {
  read?: T
  unreadable?: string
}

export async function ask<T, Section>(
  spec: Ask<Section>,
  doc: LoadedDocument,
  clock: AbortController,
  outer: AbortSignal | undefined,
  trouble: string[],
  label: string,
  /**
   * Which rung of the ladder answered last time, for this endpoint, in this
   * read.
   *
   * An endpoint's structured-output support does not vary between one question
   * and the next, so paying to discover it on every call is paying for the same
   * answer repeatedly. On the first real trial this cost the whole document: the
   * model behind the default slug pads its reply with whitespace for minutes
   * when asked for a JSON schema, and answers a JSON-object request with one
   * empty token, so every call spent a minute and a half being refused twice
   * before asking the way that works.
   *
   * Remembered rather than configured, because it is a fact about an endpoint
   * discovered by asking it, and it resets with every read, so an endpoint that
   * gains support does not stay demoted.
   */
  ladder: Ladder,
  call: Caller,
): Promise<Answered<T>> {
  /**
   * Three ways of asking, weakest constraint last, named by number.
   *
   * The shapes themselves are built server-side, because each carries the
   * schema and the schema is part of the question. What stays here is the
   * walking of the ladder: which rung to start on is a fact about an endpoint
   * learned over a document, and the patience for each attempt belongs with
   * whoever is waiting for it.
   */
  const RUNGS = 3

  /**
   * Three ways of asking, weakest constraint last.
   *
   * Endpoints vary in what they implement and in how they say so. Some refuse a
   * request naming a `response_format` they do not support; OpenRouter instead
   * drops the parameter and answers anyway, which looks like success and reads
   * like prose. So an attempt counts only when it comes back parseable, and the
   * ladder is walked until one does.
   */

  for (let i = 0; i < RUNGS; i++) {
    if (i < ladder.from) continue
    if (clock.signal.aborted) return {}
    const startedAt = Date.now()
    if (TRACE) console.error(`  -> ${label} attempt ${i + 1}`)

    /**
     * The call and the reading of it, together, because both fail the same way.
     *
     * A gateway that closes the connection partway through a reply throws from
     * `json()` rather than from `fetch`, and that read sat outside this catch:
     * one dropped socket ended the whole document, not the attempt. It happened
     * on the first request of the first real trial, where the schema attempt
     * was answered with a closed connection and the two fallbacks below, which
     * send no schema at all, were never tried.
     */
    let payload: Answered<T>
    /**
     * This attempt's own clock, tripped by the shared one as well.
     *
     * Both matter: the shared clock is the reader's patience with the whole
     * document, and this is the reader's patience with one question. Aborting
     * here leaves the shared one running, so the next rung is asked
     * immediately rather than inheriting the time this one wasted.
     */
    const attemptClock = new AbortController()
    const patience = ladder.proven && i === ladder.from ? PROVEN_MS : PROBE_MS
    const abandon = setTimeout(() => attemptClock.abort(), patience)
    const stop = () => attemptClock.abort()
    clock.signal.addEventListener('abort', stop)

    try {
      // No model named: which one to ask is the deployment's decision, made
      // where the key that pays for it is held.
      const response = await call('read', { ...spec, doc, rung: i }, attemptClock.signal)

      if (!response.ok) {
        const said = await response.text().catch(() => '')
        trouble.push(
          `${label} attempt ${i + 1} answered ${response.status} ${said.slice(0, 160)}`.trim(),
        )
        /**
         * A refusal that no rung fixes, so the ladder stops rather than
         * spending two more attempts learning the same thing. The daily cap and
         * a deployment with no model configured are both states of this
         * account or this deployment, not of the request shape.
         */
        if (response.status === 429 || response.status === 503) break
        continue
      }

      payload = (await response.json()) as Answered<T>
    } catch (thrown) {
      if (outer?.aborted) throw thrown
      // Next attempt, not the end of the read. Each one asks differently, and
      // the reason this one failed may be the thing the next one does not send.
      trouble.push(
        attemptClock.signal.aborted && !clock.signal.aborted
          ? `${label} attempt ${i + 1} was still unanswered after ${patience / 1000}s`
          : `${label} attempt ${i + 1} did not come back: ${thrown instanceof Error ? thrown.message : 'unknown'}`,
      )
      if (TRACE)
        console.error(
          `  !! ${label} attempt ${i + 1} failed after ${Math.round((Date.now() - startedAt) / 1000)}s`,
        )
      continue
    } finally {
      clearTimeout(abandon)
      clock.signal.removeEventListener('abort', stop)
    }

    if (TRACE)
      console.error(
        `  <- ${label} attempt ${i + 1} in ${Math.round((Date.now() - startedAt) / 1000)}s`,
      )

    if (payload.read !== undefined) {
      ladder.from = i
      ladder.proven = true
      return payload
    }

    /**
     * Said by the endpoint rather than worked out here, because working it out
     * needs the reply. A reasoning model keeps its thinking in its own field
     * and answers in `content` like any other, and an empty `content` beside a
     * full `reasoning` reads as "the endpoint said nothing" when it is not.
     * Telling those apart means looking at the shape of the reply, which is
     * exactly the knowledge that does not live here any more.
     */
    trouble.push(`${label} attempt ${i + 1} ${payload.unreadable ?? 'replied with nothing usable'}`)
  }

  return {}
}


/**
 * Which rung answered, remembered for as long as the page is open.
 *
 * An endpoint's structured-output support is a property of the endpoint, not of
 * the question, so discovering it once and keeping it is the difference between
 * a document costing one discovery and costing one per cover.
 */
export interface Ladder {
  from: number
  proven: boolean
}

export const newLadder = (): Ladder => ({ from: 0, proven: false })

/**
 * How many questions to have outstanding at once.
 *
 * A schedule listing thirteen parts would otherwise open thirteen connections,
 * and a free tier answers such a burst with rate limits. Sequential would be
 * safe and slow enough to hit the deadline on a long document, so it is
 * neither.
 */
export const AT_ONCE = 3

export async function inBatches<T, R>(items: T[], size: number, run: (item: T) => Promise<R>): Promise<R[]> {
  const out: R[] = []
  for (let i = 0; i < items.length; i += size) {
    out.push(...(await Promise.all(items.slice(i, i + size).map(run))))
  }
  return out
}
